import { state$ as authState$ } from "@internal/auth";
import { supabase } from "@internal/supabase-client";
import type { RxCollection } from "rxdb";
import {
  type RxReplicationState,
  replicateRxCollection,
} from "rxdb/plugins/replication";
import { BehaviorSubject, filter, firstValueFrom, Subject } from "rxjs";
import type {
  BaseRecord,
  Checkpoint,
  SyncConnection,
  SyncState,
} from "./types";

const MODIFIED = "_modified";
const DELETED = "_deleted";

/**
 * How long a sync may take before we announce it. A push that lands inside
 * this window is not worth a «Lagrer …» flash, and without it every document
 * arriving from the server would blink the indicator.
 */
const PENDING_ANNOUNCE_DELAY_MS = 200;

/**
 * How long to let supabase-js rejoin a dropped channel on its own before
 * tearing it down and building a fresh one. The client has its own rejoin
 * timer; racing it just produces duplicate channels.
 */
const CHANNEL_REBUILD_DELAY_MS = 10_000;

const SYNCED: SyncState = {
  connection: "connected",
  pending: false,
  lastInSyncAt: null,
};

/**
 * Replication state as written by whichever tab is the elected leader.
 *
 * Only the leader replicates: two replications over one IndexedDB race on the
 * shared metadata and produce write conflicts. So the other tabs cannot answer
 * "is my work saved" themselves — they read the leader's answer from a local
 * document, which RxDB broadcasts across tabs. `connection` is still decided
 * per tab, because `navigator.onLine` is a property of the tab, not the store.
 */
interface SharedSyncState {
  /**
   * Null until the leader has reported. Distinct from `false` on purpose: a
   * follower whose leader has not spoken yet knows nothing, and a leader
   * running an older build never writes here at all. Treating that silence as
   * "disconnected" made a perfectly healthy tab claim to be offline forever.
   */
  channelSubscribed: boolean | null;
  pending: boolean;
  failed: boolean;
  lastInSyncAt: string | null;
}

const INITIAL_SHARED: SharedSyncState = {
  channelSubscribed: null,
  pending: false,
  failed: false,
  lastInSyncAt: null,
};

interface SupabaseRow extends BaseRecord {
  _modified: string;
  _deleted: boolean;
}

interface PullDoc extends BaseRecord {
  _deleted: boolean;
}

function rowToDoc(row: SupabaseRow): PullDoc {
  const { _modified, _deleted, ...rest } = row;
  void _modified;
  return { ...rest, _deleted: !!_deleted } as PullDoc;
}

function docToRow(doc: PullDoc): Record<string, unknown> {
  const { _deleted, ...rest } = doc as PullDoc & {
    _meta?: unknown;
    _rev?: unknown;
    _attachments?: unknown;
  };
  const cleaned: Record<string, unknown> = { ...rest, [DELETED]: !!_deleted };
  delete cleaned._meta;
  delete cleaned._rev;
  delete cleaned._attachments;
  delete cleaned[MODIFIED];
  return cleaned;
}

export interface ReplicationHandle {
  /** Derived state for the sync indicator. See {@link SyncState}. */
  syncState$: import("rxjs").Observable<SyncState>;
  awaitInitialReplication: () => Promise<void>;
  getErrors: () => string[];
  clearErrors: () => void;
}

export interface StartReplicationOptions<TDoc extends BaseRecord> {
  table: string;
  getCollection: (userId: string) => Promise<RxCollection<TDoc>>;
  onError?: (err: unknown) => void;
  /**
   * Disable for E2E tests. When true, replication is skipped and
   * awaitInitialReplication resolves immediately.
   */
  disabled?: boolean;
}

export function startReplication<TDoc extends BaseRecord>(
  options: StartReplicationOptions<TDoc>,
): ReplicationHandle {
  const { table, getCollection, onError, disabled } = options;

  const SHARED_DOC_ID = `sync-state-${table}`;

  const syncState$ = new BehaviorSubject<SyncState>(SYNCED);
  const initialReady$ = new BehaviorSubject<boolean>(false);
  const errors: string[] = [];

  let currentState: RxReplicationState<PullDoc, Checkpoint> | null = null;
  let currentChannel: ReturnType<typeof supabase.channel> | null = null;
  let currentUserId: string | null = null;
  let currentResync: (() => void) | null = null;
  let onlineListenersAttached = false;

  let shared: SharedSyncState = { ...INITIAL_SHARED };

  function online(): boolean {
    return typeof navigator === "undefined" ? true : navigator.onLine;
  }

  function connection(): SyncConnection {
    // This tab's own network beats anything the leader last reported, and it
    // explains every other symptom, so it is checked first.
    if (!online()) return "offline";
    // With replication off there is no channel to judge, and losing the
    // network is still worth reporting — the indicator is the only thing
    // telling the user whether updates can reach them.
    if (disabled) return "connected";
    if (shared.channelSubscribed === null) return "connecting";
    if (!shared.channelSubscribed) return "offline";
    if (shared.failed) return "error";
    return "connected";
  }

  function publishSyncState(): void {
    const conn = connection();
    // While connected the stamp is always ~now and the indicator hides it, so
    // publishing it would defeat the comparison below and re-emit on every
    // write. It becomes information only once the guarantee breaks, frozen at
    // the moment it did.
    const next: SyncState = {
      connection: conn,
      pending: shared.pending,
      lastInSyncAt: conn === "connected" ? null : shared.lastInSyncAt,
    };
    const prev = syncState$.value;
    if (
      prev.connection === next.connection &&
      prev.pending === next.pending &&
      prev.lastInSyncAt === next.lastInSyncAt
    ) {
      return;
    }
    syncState$.next(next);
  }

  function attachOnlineListeners(onOnline?: () => void): void {
    if (onlineListenersAttached) return;
    if (typeof window === "undefined") return;
    onlineListenersAttached = true;
    // Visibility is deliberately not handled here: RxDB's
    // `toggleOnDocumentVisible` (on by default) already pauses a hidden tab
    // and re-syncs when it comes back. Network transitions it does not know
    // about, so those stay ours.
    window.addEventListener("online", () => {
      publishSyncState();
      onOnline?.();
    });
    window.addEventListener("offline", publishSyncState);
  }

  if (disabled) {
    initialReady$.next(true);
    attachOnlineListeners();
    publishSyncState();
    return {
      syncState$: syncState$.asObservable(),
      awaitInitialReplication: async () => {},
      getErrors: () => [...errors],
      clearErrors: () => {
        errors.length = 0;
      },
    };
  }

  async function teardown(): Promise<void> {
    if (currentChannel) {
      await supabase.removeChannel(currentChannel);
      currentChannel = null;
    }
    if (currentState) {
      await currentState.cancel();
      currentState = null;
    }
    currentResync = null;
    shared = { ...INITIAL_SHARED };
  }

  authState$.subscribe(async (s) => {
    if (s.loading) return;
    const userId = s.user?.id ?? null;
    if (userId === currentUserId) return;
    currentUserId = userId;

    await teardown();
    initialReady$.next(false);
    publishSyncState();
    if (!userId) return;

    const collection = await getCollection(userId);
    const database = collection.database;

    // Every tab reads the leader's answer, including the leader itself, so
    // there is exactly one path from state to display.
    collection.getLocal$<SharedSyncState>(SHARED_DOC_ID).subscribe((doc) => {
      shared = doc ? { ...INITIAL_SHARED, ...doc.toJSON().data } : shared;
      publishSyncState();
    });

    async function writeShared(patch: Partial<SharedSyncState>): Promise<void> {
      const next = { ...shared, ...patch };
      if (
        next.channelSubscribed === shared.channelSubscribed &&
        next.pending === shared.pending &&
        next.failed === shared.failed &&
        next.lastInSyncAt === shared.lastInSyncAt
      ) {
        return;
      }
      shared = next;
      publishSyncState();
      try {
        await collection.upsertLocal(SHARED_DOC_ID, next);
      } catch {
        // A losing write means another tab got there first; its state is as
        // good as ours and will arrive through the subscription above.
      }
    }

    const pullStream$ = new Subject<
      { checkpoint: Checkpoint; documents: PullDoc[] } | "RESYNC"
    >();

    const state = replicateRxCollection<PullDoc, Checkpoint>({
      collection: collection as unknown as Parameters<
        typeof replicateRxCollection<PullDoc, Checkpoint>
      >[0]["collection"],
      replicationIdentifier: `${table}-supabase-${userId}`,
      live: true,
      pull: {
        batchSize: 50,
        async handler(checkpoint, batchSize) {
          let q = supabase.from(table).select("*");
          if (checkpoint) {
            const { modified, id } = checkpoint;
            q = q.or(
              `${MODIFIED}.gt.${modified},and(${MODIFIED}.eq.${modified},id.gt.${id})`,
            );
          }
          q = q
            .order(MODIFIED, { ascending: true })
            .order("id", { ascending: true })
            .limit(batchSize);
          const { data, error } = await q;
          if (error) throw error;
          const rows = (data ?? []) as SupabaseRow[];
          const last = rows[rows.length - 1];
          const newCheckpoint: Checkpoint | undefined = last
            ? { id: last.id, modified: last[MODIFIED] }
            : (checkpoint ?? undefined);
          return {
            documents: rows.map(rowToDoc),
            checkpoint: newCheckpoint as Checkpoint,
          };
        },
        stream$: pullStream$.asObservable(),
      },
      push: {
        batchSize: 50,
        async handler(rows) {
          const toUpsert = rows.map((r) => docToRow(r.newDocumentState));
          const { error } = await supabase
            .from(table)
            .upsert(toUpsert, { onConflict: "id" });
          if (error) throw error;
          // Reporting no conflicts is a blind overwrite: see issue #65.
          return [];
        },
      },
    });

    state.error$.subscribe((err) => {
      void writeShared({ failed: true });
      const msg =
        err instanceof Error
          ? err.message
          : String((err as { message?: unknown })?.message ?? err);
      errors.push(msg);
      console.error("[replication]", err);
      /**
       * Only escalate a genuine failure. A push that fails because the device
       * is offline is expected, self-healing, and repeats on every retry — a
       * few minutes on a train would bury the real errors and burn the Sentry
       * quota. The banner already tells the user; nobody needs paging.
       */
      if (online()) onError?.(err);
    });

    // «Alt lagret» must never appear while a local change is unconfirmed, so
    // the flag is driven by RxDB's own in-sync check rather than by whether a
    // cycle happens to be running. When offline, `awaitInSync()` simply does
    // not resolve, which is the honest answer.
    let pendingToken = 0;
    function trackInSync(): void {
      const token = ++pendingToken;
      const announce = setTimeout(() => {
        if (token !== pendingToken) return;
        void writeShared({ pending: true });
      }, PENDING_ANNOUNCE_DELAY_MS);

      state
        .awaitInSync()
        .then(() => {
          if (token !== pendingToken) return;
          clearTimeout(announce);
          void writeShared({
            pending: false,
            failed: false,
            lastInSyncAt: new Date().toISOString(),
          });
        })
        .catch(() => {
          clearTimeout(announce);
        });
    }

    function buildChannel(): ReturnType<typeof supabase.channel> {
      let rebuildTimer: ReturnType<typeof setTimeout> | null = null;

      const channel = supabase
        .channel(`sync-${table}`)
        .on(
          "postgres_changes",
          { event: "*", schema: "public", table },
          (payload) => {
            // Clients cannot hard-delete — no such policy exists — so a
            // removal arrives as an UPDATE carrying `_deleted = true`. A
            // DELETE here can only be an admin or service-role wipe, and
            // Realtime forwards those to everyone because the old row carries
            // just its key and cannot be RLS-filtered. Ignoring it leaves a
            // stale local copy, which is the lesser harm: the branch that
            // used to handle it invented a checkpoint from the client clock,
            // which could skip past rows and hide them until something
            // touched them again.
            if (payload.eventType === "DELETE") return;
            const row = payload.new as SupabaseRow;
            pullStream$.next({
              checkpoint: { id: row.id, modified: row[MODIFIED] },
              documents: [rowToDoc(row)],
            });
          },
        )
        .subscribe((status) => {
          const subscribed = status === "SUBSCRIBED";
          const wasSubscribed = shared.channelSubscribed;
          void writeShared({ channelSubscribed: subscribed });

          if (subscribed) {
            if (rebuildTimer) {
              clearTimeout(rebuildTimer);
              rebuildTimer = null;
            }
            // Anything that changed while the channel was down was never
            // delivered, so catch up from the checkpoint.
            if (!wasSubscribed) pullStream$.next("RESYNC");
          } else if (
            status === "CHANNEL_ERROR" ||
            status === "TIMED_OUT" ||
            status === "CLOSED"
          ) {
            if (!rebuildTimer) {
              rebuildTimer = setTimeout(() => {
                rebuildTimer = null;
                if (shared.channelSubscribed || currentChannel !== channel) {
                  return;
                }
                void supabase.removeChannel(channel).then(() => {
                  if (currentChannel !== channel) return;
                  currentChannel = buildChannel();
                });
              }, CHANNEL_REBUILD_DELAY_MS);
            }
          }
        });

      return channel;
    }

    currentState = state;

    /**
     * Everything that talks to the server belongs to the leader alone: a
     * second replication over the same store would race it, and a second
     * websocket would deliver documents the leader has already written.
     * `waitForLeadership` is left at its RxDB default of true for the same
     * reason, so this promise and the replication start together.
     */
    void database.waitForLeadership().then(() => {
      if (currentState !== state) return;
      /**
       * The document survives a reload, so it may still hold the last
       * session's answer. Invalidate before subscribing: an unknown state is
       * correct for the moment between taking leadership and the channel
       * reporting, and a stale `true` would claim delivery we cannot back.
       */
      void writeShared({ channelSubscribed: null, failed: false });
      currentChannel = buildChannel();
      currentResync = () => pullStream$.next("RESYNC");
      const changeSub = collection.$.subscribe((event) => {
        /**
         * Local documents are not work to track, and must not re-enter here.
         * The one local document this collection holds is the very thing
         * trackInSync writes, and its lastInSyncAt stamp is a fresh timestamp
         * on every call — so writeShared's write-if-changed guard can never
         * bail, and each write produced the event that produced the next one.
         * That loop wrote to IndexedDB continuously on a completely idle tab,
         * starving every live query in both apps.
         *
         * Nothing is lost by skipping them: local documents are never
         * replicated, so they can never leave anything unsent.
         */
        if (event.isLocal) return;
        trackInSync();
      });
      state.onCancel.push(() => changeSub.unsubscribe());
      trackInSync();
    });

    attachOnlineListeners(() => currentResync?.());
    state.awaitInitialReplication().then(() => initialReady$.next(true));
    console.log(`[rxdb] replication started for user ${userId} (${table})`);
  });

  async function awaitInitialReplication(): Promise<void> {
    if (initialReady$.value) return;
    await firstValueFrom(initialReady$.pipe(filter(Boolean)));
  }

  return {
    syncState$: syncState$.asObservable(),
    awaitInitialReplication,
    getErrors: () => [...errors],
    clearErrors: () => {
      errors.length = 0;
    },
  };
}
